import React, { useState } from 'react';
import type { ProjectDetails, ProjectTeamMember } from '@/types/project';
import type { DataMode } from '@/contexts/DataSourceContext';
import { projectsApi, type ProjectListItem } from '@/services/projectsApi';
import { timesheetApi } from '@/services/timesheetApi';
import type { CostCentre } from '@/types/timesheet';
import { toast } from 'sonner';

interface UseProjectDetailsDialogOptions {
  screenMode: DataMode;
  /** Lets the caller reflect the freshly loaded team count on its list item. */
  onTeamMembersLoaded: (projectId: string, teamMembers: ProjectTeamMember[]) => void;
}

export interface UseProjectDetailsDialogResult {
  selectedProject: ProjectDetails | null;
  showDetailsDialog: boolean;
  setShowDetailsDialog: (open: boolean) => void;
  projectCostCentres: CostCentre[];
  setProjectCostCentres: React.Dispatch<React.SetStateAction<CostCentre[]>>;
  isLoadingCostCentres: boolean;
  isLoadingTeamMembers: boolean;
  handleViewDetails: (project: ProjectDetails | ProjectListItem) => void;
}

/** Owns the selected project, its dialog visibility and the detail fetches. */
export const useProjectDetailsDialog = ({
  screenMode,
  onTeamMembersLoaded,
}: UseProjectDetailsDialogOptions): UseProjectDetailsDialogResult => {
  const [selectedProject, setSelectedProject] = useState<ProjectDetails | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [projectCostCentres, setProjectCostCentres] = useState<CostCentre[]>([]);
  const [isLoadingCostCentres, setIsLoadingCostCentres] = useState(false);

  // Handle viewing project details - fetches full details for ProjectListItem in live mode
  const handleViewDetails = (project: ProjectDetails | ProjectListItem) => {
    if (screenMode === 'live') {
      // In live mode, we have ProjectListItem, need to fetch full details
      setIsLoadingTeamMembers(true);
      projectsApi.getProjectById(project.id)
        .then(fullProject => {
          setSelectedProject(fullProject);
          setShowDetailsDialog(true);
          // Fetch cost centres for this project
          setIsLoadingCostCentres(true);
          timesheetApi.getCostCentresByProjectCode(fullProject.code)
            .then(costCentres => setProjectCostCentres(costCentres))
            .catch(error => {
              console.error('Failed to fetch cost centres:', error);
              setProjectCostCentres([]);
            })
            .finally(() => setIsLoadingCostCentres(false));
          // Also fetch team members
          return projectsApi.getProjectTeamMembers(project.id);
        })
        .then(teamMembers => {
          setSelectedProject(prev => prev ? { ...prev, teamMembers } : null);
          onTeamMembersLoaded(project.id, teamMembers);
        })
        .catch(error => {
          console.error('Failed to fetch project details:', error);
          toast.error('Failed to load project details');
        })
        .finally(() => {
          setIsLoadingTeamMembers(false);
        });
    } else {
      // In demo mode, we already have full ProjectDetails
      setSelectedProject(project as ProjectDetails);
      setShowDetailsDialog(true);
    }
  };

  return {
    selectedProject,
    showDetailsDialog,
    setShowDetailsDialog,
    projectCostCentres,
    setProjectCostCentres,
    isLoadingCostCentres,
    isLoadingTeamMembers,
    handleViewDetails,
  };
};
